import { useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LuEye, LuEyeOff, LuLock, LuMail } from "react-icons/lu";
import api from "../../api/axios";
import Navbar from "../../components/companyLandingComponents/Navbar/Navbar";
import BottomFooter from "../../components/Footer/BottomFooter/BottomFooter";
import "./auth.css";

function ResetPassword() {
  const { token: rawToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const emailFromUrl = query.get("email") || "";

  // Reason: path tokens may be URL-encoded by email clients
  const token = rawToken ? decodeURIComponent(rawToken) : "";

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsError(false);

    if (password.length < 8) {
      setMessage("Password must be at least 8 characters.");
      setIsError(true);
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      setIsError(true);
      return;
    }

    if (!token) {
      setMessage("This reset link is missing a token. Request a new one.");
      setIsError(true);
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("/api/reset-password", {
        token,
        email,
        password,
        password_confirmation: confirmPassword,
      });
      setMessage(res.data.message || "Password reset successful!");
      setIsError(false);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      const errors = err.response?.data?.errors;
      let apiMessage =
        err.response?.data?.message ||
        "This password reset link is invalid or has expired.";

      if (errors) {
        const first = Object.values(errors)[0];
        if (Array.isArray(first) && first[0]) {
          apiMessage = first[0];
        }
      }

      setMessage(apiMessage);
      setIsError(true);
      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Navbar />

      <div
        className="position-fixed start-0 end-0 top-0 bottom-0"
        style={{
          background:
            'url("/assets/images/iconicback.jpg") center/cover no-repeat',
          zIndex: 0,
        }}
      ></div>

      <div
        className="container position-relative d-flex align-items-center justify-content-center"
        style={{
          zIndex: 1,
          minHeight: "calc(100vh - 80px)",
          marginTop: "15px",
        }}
      >
        <div className="row justify-content-center w-100">
          <div className="col-xxl-8 col-lg-10">
            <div
              className="card border-0 shadow-lg overflow-hidden"
              style={{
                borderRadius: "15px",
                backdropFilter: "blur(10px)",
                backgroundColor: "rgba(255, 255, 255, 0.98)",
              }}
            >
              <div className="row g-0">
                <div
                  className="col-lg-5 d-none d-lg-flex flex-column align-items-center justify-content-center text-white p-5"
                  style={{ backgroundColor: "#2e86de" }}
                >
                  <img
                    src="/assets/images/logo.png"
                    alt="logo"
                    className="img-fluid mb-4"
                    style={{ maxWidth: "180px" }}
                  />
                  <h3 className="fw-bold text-center welcome-text">
                    Choose a New Password
                  </h3>
                  <p className="text-center opacity-75 small">
                    After resetting, you will be signed out of other sessions
                    and redirected to login.
                  </p>
                </div>

                <div className="col-lg-7 p-4 p-md-5">
                  <div className="d-lg-none text-center mb-4">
                    <img
                      src="/assets/images/logo.png"
                      alt="logo"
                      style={{ width: "120px" }}
                    />
                  </div>

                  <h2 className="fw-bold text-dark mb-1">Reset Password</h2>
                  <p className="text-muted mb-4">
                    Enter your email and a new password for your account.
                  </p>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">
                        Email Address
                      </label>
                      <div className="input-group custom-input-group">
                        <span className="input-group-text bg-light text-muted">
                          <LuMail size={18} />
                        </span>
                        <input
                          className="form-control bg-light"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@example.com"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label small fw-semibold">
                        New Password
                      </label>
                      <div className="input-group custom-input-group">
                        <span className="input-group-text bg-light text-muted">
                          <LuLock size={18} />
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control bg-light"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="At least 8 characters"
                          required
                          minLength={8}
                          disabled={loading}
                        />
                        <span
                          className="input-group-text bg-light border-start-0 text-muted"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ cursor: "pointer" }}
                        >
                          {showPassword ? (
                            <LuEyeOff size={18} />
                          ) : (
                            <LuEye size={18} />
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label small fw-semibold">
                        Confirm Password
                      </label>
                      <div className="input-group custom-input-group">
                        <span className="input-group-text bg-light text-muted">
                          <LuLock size={18} />
                        </span>
                        <input
                          type={showConfirm ? "text" : "password"}
                          className="form-control bg-light"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Repeat new password"
                          required
                          minLength={8}
                          disabled={loading}
                        />
                        <span
                          className="input-group-text bg-light border-start-0 text-muted"
                          onClick={() => setShowConfirm(!showConfirm)}
                          style={{ cursor: "pointer" }}
                        >
                          {showConfirm ? (
                            <LuEyeOff size={18} />
                          ) : (
                            <LuEye size={18} />
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        className="btn-style w-100 py-2 fw-bold shadow-sm text-center d-flex align-items-center justify-content-center"
                        type="submit"
                        disabled={loading}
                        style={{ borderRadius: "8px" }}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : null}
                        {loading ? "Updating..." : "Reset Password"}
                      </button>
                    </div>
                  </form>

                  {message && (
                    <p
                      className={`text-center mt-3 small ${
                        isError ? "text-danger" : "text-success"
                      }`}
                    >
                      {message}
                    </p>
                  )}

                  <div className="text-center mt-4">
                    <Link to="/login" className="text-primary fw-semibold small">
                      ← Back to Login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomFooter />
    </div>
  );
}

export default ResetPassword;
